import { Record } from "immutable";
import {
  StoreKeyType,
  TranslationInterface,
  DeliveraModifierGroupInterface,
  DeliveraMenuEntityInterface,
} from "./interface";
import DeliveraItem, { DeliveraItemInterface } from "./DeliveraItem";
import DeliveraCategory, {
  DeliveraCategoryInterface,
} from "./DeliveraCategorie";
import DeliveraMenu, { DeliveraMenuInterface } from "./DeliveraMenu";
import DeliveraMenuEntity from "./DeliveraEntity";

export interface DeliveraStoreInterface {
  menus?: DeliveraMenu[];
  categories: DeliveraCategory[];
  items: DeliveraItem[];
  modifier_groups: DeliveraModifierGroupInterface[];
  display_options: {
    disable_item_instructions: boolean;
  };
  provider: StoreKeyType;
  menu_type: string;
}

const defaultDeliveraStoreProps: DeliveraStoreInterface = {
  menus: [],
  categories: [],
  items: [],
  modifier_groups: [],
  display_options: {
    disable_item_instructions: false,
  },
  provider: "ubereats",
  menu_type: "MENU_TYPE_FULFILLMENT_DELIVERY",
};

export class DeliveraStore
  extends Record(defaultDeliveraStoreProps)
  implements DeliveraStoreInterface {
  constructor(props: any, provider: StoreKeyType) {
    super(DeliveraStore.reveiveProps(props, provider));
  }

  private static reveiveProps(props: any, provider: StoreKeyType) {
    let data = Object.assign({}, props);
    data["provider"] = provider;
    data["menus"] = ((props["menus"] ?? []) as any[]).map((m) => {
      if ((m.id as string).length === 0) {
        m.id = `_${new Date().getTime()}`;
      }
      return new DeliveraMenu(m);
    });
    data["categories"] = ((props["categories"] ?? []) as []).map(
      (c) => new DeliveraCategory(c)
    );
    data["items"] = ((props["items"] ?? []) as []).map(
      (i) => new DeliveraItem(i)
    );
    data["modifier_groups"] =
      props["modifier_groups"] !== null &&
      props["modifier_groups"] !== undefined
        ? props["modifier_groups"]
        : [];
    return data;
  }

  public getItemInCategory(category: DeliveraCategory): DeliveraItem[] {
    const a: string[] = category.entities?.map((e) => e.id);
    return this.items
      .filter((i) => a.indexOf(i.id) !== -1)
      .sort((a, b) => {
        const nameA = DeliveraStore.retreiveNameFromTranslation(
          a.title
        ).toLocaleLowerCase();
        const nameB = DeliveraStore.retreiveNameFromTranslation(
          b.title
        ).toLocaleLowerCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
  }

  public static retreiveNameFromTranslation(
    translation: TranslationInterface | undefined
  ): string {
    if (undefined === translation || null === translation) {
      return "";
    }
    const obj = translation.translations;
    if (obj.fr !== undefined && obj.fr !== null) return obj.fr;

    const keys = Object.keys(obj);
    return (obj as any)[keys[0]];
  }

  public getGroupeOptionFromIds(
    ids: string[]
  ): DeliveraModifierGroupInterface[] {
    return this.modifier_groups.filter((m) => ids.indexOf(m.id) !== -1);
  }

  public getOptionsOnGroupeOptionIds(
    groupeOptionIds: DeliveraMenuEntityInterface[]
  ): DeliveraItem[] {
    return this.items.filter(
      (i) => groupeOptionIds.findIndex((g) => g.id === i.id) !== -1
    );
  }

  public getItemWithoutGroupe(): DeliveraItem[] {
    const allIds = this.categories.reduce(
      (accu: string[], c) => accu.concat(c.entities.map((e) => e.id)),
      []
    );
    return this.items
      .filter((i) => allIds.indexOf(i.id) === -1)
      .sort((a, b) => {
        const nameA = DeliveraStore.retreiveNameFromTranslation(
          a.title
        ).toLocaleLowerCase();
        const nameB = DeliveraStore.retreiveNameFromTranslation(
          b.title
        ).toLocaleLowerCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
  }

  public isOption(item: DeliveraItem) {
    return (
      this.getItemWithoutGroupe().find((i) => i.id === item.id) !== undefined
    );
  }

  // CATEGORIE UPDATE

  createCategory(category: DeliveraCategoryInterface) {
    category.id = `cat_${new Date().getTime()}`;
    return this.update("categories", (cats) =>
      cats.concat([new DeliveraCategory(category)])
    );
  }

  updateCatgeory(updatedCategory: DeliveraCategoryInterface): DeliveraStore {
    return this.update("categories", (categories) => {
      const index = categories.findIndex((c) => c.id === updatedCategory.id);
      if (-1 === index) {
        throw new Error("Catgorie not found");
      }
      const updatedCategorie = categories[index].merge(updatedCategory);
      categories[index] = updatedCategorie;
      return categories;
    });
  }

  removeItemFromCategorie(
    item: DeliveraItemInterface,
    catgeory: DeliveraCategoryInterface
  ): DeliveraStore {
    return this.update("categories", (categories) => {
      const index = categories.findIndex((c) => c.id === catgeory.id);
      if (index === -1) {
        throw new Error("Catgorie not found");
      }
      let indexItem = categories[index].entities.findIndex(
        (e) => e.id === item.id
      );
      if (indexItem === -1) {
        throw new Error("Item not found");
      }
      const updatedCategorie = categories[index].update(
        "entities",
        (entities) => {
          entities.splice(indexItem, 1);
          return entities;
        }
      );
      categories[index] = updatedCategorie;
      return categories;
    });
  }

  // ITEM UPDATE FUNC

  updateItem(itemUpdated: DeliveraItemInterface) {
    // console.log(itemUpdated);
    return this.update("items", (allItems) => {
      const index = allItems.findIndex((i) => i.id === itemUpdated.id);
      if (-1 === index) {
        throw new Error("Item not found");
      }
      allItems[index] = allItems[index].merge(itemUpdated);
      return allItems;
    });
  }

  addItem(item: DeliveraItemInterface): DeliveraStore {
    if (item.id === null || item.id === undefined) {
      item.id = `item_${new Date().getTime()}`;
    }
    return this.update("items", (items) =>
      items.concat([new DeliveraItem(item)])
    );
  }

  addItemInCategorie(
    item: DeliveraItemInterface,
    category: DeliveraCategoryInterface
  ): DeliveraStore {
    item["id"] = `${new Date().getTime()}`;
    const withNewItem = this.update("items", (items) => {
      items.push(new DeliveraItem(item));
      return items;
    });

    return withNewItem.update("categories", (categories) => {
      const index = categories.findIndex((c) => c.id === category.id);
      if (index === -1) {
        throw new Error("Catgorie not found");
      }
      const updatedCatgorie = categories[index].update(
        "entities",
        (entities) => {
          entities.push(new DeliveraMenuEntity({ id: item.id, type: "ITEM" }));
          return entities;
        }
      );
      categories[index] = updatedCatgorie;
      return categories;
    });
  }

  removeItem(item: DeliveraItemInterface): DeliveraStore {
    return this.update("items", (items) => {
      const index = items.findIndex((i) => i.id === item.id);
      if (index === -1) {
        throw new Error("item not found");
      }
      items.splice(index, 1);
      return items;
    });
  }

  // GROUPE MODIIFERS

  addGroupeModifier(groupeModifier: DeliveraModifierGroupInterface) {
    return this.update("modifier_groups", (modifierGroupes) => {
      if (groupeModifier.id === undefined || groupeModifier.id.length === 0) {
        groupeModifier.id = `groupe_modifiers_${new Date().getTime()}`;
      }
      modifierGroupes.push(groupeModifier);
      return modifierGroupes;
    });
  }

  removeGroupeModifierFromItem(
    groupeModifier: DeliveraModifierGroupInterface,
    item: DeliveraItemInterface
  ): DeliveraStore {
    return this.update("items", (items) => {
      const itemIndex = items.findIndex((i) => i.id === item.id);
      if (itemIndex === -1) {
        throw new Error("Item not found");
      }
      const groupeIndex = items[itemIndex].modifier_group_ids!.ids.findIndex(
        (m) => m === groupeModifier.id
      );
      if (groupeIndex === -1) {
        throw new Error("Groupe Modifier not found");
      }
      const itemUpdated = items[itemIndex].update(
        "modifier_group_ids",
        (someIds) => {
          someIds?.ids.splice(groupeIndex, 1);
          return someIds;
        }
      );
      items[itemIndex] = itemUpdated;
      return items;
    });
  }

  updateGroupeModifier(
    groupeModifier: DeliveraModifierGroupInterface
  ): DeliveraStore {
    return this.update("modifier_groups", (modifierGroupes) => {
      const index = modifierGroupes.findIndex(
        (i) => i.id === groupeModifier.id
      );
      if (index === -1) {
        throw new Error("Modifiers groupe not fpund");
      }
      modifierGroupes[index] = groupeModifier;

      return modifierGroupes;
    });
  }

  // Menu

  updateMenu(menuUpdated: DeliveraMenuInterface) {
    return this.update("menus", (menus) => {
      const index = menus?.findIndex((m) => m.id === menuUpdated.id);
      if (index === -1 || index === undefined) {
        throw new Error("Menu not found");
      }

      menus![index] = menus![index].merge(menuUpdated);

      return menus;
    });
  }

  deleteMenu(menuSelected: DeliveraMenuInterface) {
    return this.update("menus", (menus) => {
      const index = menus?.findIndex((m) => m.id === menuSelected.id);
      if (index === -1 || index === undefined) {
        throw new Error("Menu not found");
      }
      menus!.splice(index, 1);

      return menus;
    });
  }

  // prepare For send

  public prepareForSend(): any {
    if (this.provider === "ubereats") {
      const data = this.toJS();
      delete (data as any).provider;
      return data;
    }
  }
}
